<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const emit = defineEmits(['close', 'createAssistant']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { checkPermissions } = usePolicy();
const canManageAssistant = computed(() => checkPermissions(['administrator']));

const assistants = useMapGetter('captainAssistants/getRecords');

const currentAssistantId = computed(() => route.params.assistantId);
const switchingAssistantId = ref(null);

const isAssistantActive = assistant => {
  return assistant.id === Number(currentAssistantId.value);
};

const handleAssistantChange = async assistant => {
  if (isAssistantActive(assistant)) return;

  const currentRouteName = route.name;
  const targetRouteName =
    currentRouteName || 'captain_assistants_experience_index';

  switchingAssistantId.value = assistant.id;
  try {
    const navigationFailure = await router.push({
      name: targetRouteName,
      params: {
        accountId: route.params.accountId,
        assistantId: assistant.id,
      },
      query: route.query,
    });

    if (!navigationFailure) emit('close');
  } catch (error) {
    useAlert(
      error?.message || t('CAPTAIN_EXPERIENCE.WORKSPACE.SWITCH_AGENT_ERROR')
    );
  } finally {
    switchingAssistantId.value = null;
  }
};

const openCreateAssistantDialog = () => {
  if (!canManageAssistant.value) return;

  emit('createAssistant');
  emit('close');
};

const openAssistantDirectory = async () => {
  try {
    const navigationFailure = await router.push({
      name: 'captain_assistants_create_index',
      params: { accountId: route.params.accountId },
    });

    if (!navigationFailure) emit('close');
  } catch (error) {
    useAlert(
      error?.message || t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_LOAD_ERROR')
    );
  }
};
</script>

<template>
  <div
    class="absolute z-50 flex w-[min(27.5rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] flex-col gap-4 rounded-xl bg-n-alpha-3 pb-3 pt-5 shadow-md outline outline-1 outline-n-container backdrop-blur-[100px] max-md:max-h-[calc(100dvh-5rem)] max-md:overflow-y-auto"
  >
    <div
      class="flex flex-col items-stretch justify-between gap-3 border-b border-n-alpha-2 px-4 pb-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6"
    >
      <div class="flex min-w-0 flex-1 flex-col gap-1">
        <div class="flex items-center gap-2">
          <h2 class="min-w-0 text-base font-medium text-n-slate-12">
            <button
              type="button"
              data-testid="assistant-directory-title"
              class="max-w-full truncate rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              :title="t('CAPTAIN.ASSISTANT_SWITCHER.OPEN_DIRECTORY')"
              :aria-label="t('CAPTAIN.ASSISTANT_SWITCHER.OPEN_DIRECTORY')"
              @click="openAssistantDirectory"
            >
              {{ t('CAPTAIN.ASSISTANT_SWITCHER.ASSISTANTS') }}
            </button>
          </h2>
        </div>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANT_SWITCHER.SWITCH_ASSISTANT') }}
        </p>
      </div>
      <Button
        v-if="canManageAssistant"
        :label="t('CAPTAIN.ASSISTANT_SWITCHER.NEW_ASSISTANT')"
        color="slate"
        icon="i-lucide-plus"
        size="sm"
        class="w-full !bg-n-alpha-2 hover:!bg-n-alpha-3 sm:w-auto"
        @click="openCreateAssistantDialog"
      />
    </div>
    <div v-if="assistants.length > 0" class="flex flex-col gap-2 px-3 sm:px-4">
      <Button
        v-for="assistant in assistants"
        :key="assistant.id"
        :label="assistant.name"
        variant="ghost"
        color="slate"
        trailing-icon
        :icon="isAssistantActive(assistant) ? 'i-lucide-check' : ''"
        class="!justify-end !px-2 !py-2 hover:!bg-n-alpha-2 [&>.i-lucide-check]:text-n-teal-10 h-9"
        size="sm"
        :is-loading="switchingAssistantId === assistant.id"
        :disabled="Boolean(switchingAssistantId)"
        @click="handleAssistantChange(assistant)"
      >
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ assistant.name || '' }}
        </span>
        <Avatar
          v-if="assistant"
          :name="assistant.name"
          :size="20"
          icon-name="i-lucide-bot"
          rounded-full
        />
      </Button>
    </div>
    <div v-else class="flex flex-col items-center gap-2 px-4 py-3">
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANT_SWITCHER.EMPTY_LIST') }}
      </p>
    </div>
    <div class="border-t border-n-alpha-2 px-3 pt-3 sm:px-4">
      <Button
        data-testid="assistant-directory-cta"
        type="button"
        color="slate"
        variant="ghost"
        icon="i-lucide-layout-grid"
        :label="t('CAPTAIN.ASSISTANT_SWITCHER.VIEW_ALL_ASSISTANTS')"
        class="w-full !justify-center"
        @click="openAssistantDirectory"
      />
    </div>
  </div>
</template>
